import type {
  Address,
  AddressAddressUspsStandardTypeEnum,
  ApplicantSectionWriteRequestV2,
  EmailAddress,
  Employment,
  EmploymentEmploymentTypeIdEnum,
  EmploymentIncomeFrequencyTypeIdEnum,
  Phone,
} from "@credit-acceptance/step6-bff-client";
import { ApplicantSectionWriteRequestV2DeleteSlotsEnum } from "@credit-acceptance/step6-bff-client";

import {
  EXTENSION_MASK_PLACEHOLDER,
  EXTENSION_TEXT_PLACEHOLDER,
  PHONE_MASK_PLACEHOLDER,
  stripMaskPlaceholder,
} from "./constants";
import type { BankAccountInput, CustomerInfoInput, IncomeInput, PersonInput, PhoneInput } from "./Signers.schema";

// ---------------------------------------------------------------------------
// Signers.putMapping
//
// Maps a form PersonInput (driver or 2nd signer) onto the BFF
// ApplicantSectionWriteRequestV2 for the Dashboard-shaped v2 save endpoint
// (PUT /api/v2/workbench/applications/{approvalId}/Dashboard). Pure
// functions — no React — so they stay unit-testable and reusable for both
// signer legs.
//
// Unlike the retired v1 SignerApplicantPutRequest (nested applicant/
// addresses/phones/emails/employments/bank sub-objects with string enums),
// the v2 request is flat and typed with the SAME numeric enum ids the GET
// side already reads (see incomeMapping.ts / residenceMapping.ts) —
// phoneUsageTypeId, employmentTypeId, incomeFrequencyTypeId — so this module
// mirrors those read-side mappings in reverse. Physical/mailing addresses are
// dedicated `current`/`mailing` slots (not a positional/typed array), matching
// `ResidenceSectionV2.current`/`.mailing` on the GET side.
//
// Only fields that exist in the form AND are BFF-required today are mapped.
// Every mapped field is always sent, blank or not (the BFF treats an omitted
// field as "no change" and an explicit "" as "clear" — see
// mapBankAccountFields' isBankNameOptional handling — so omitting a blank
// field would silently leave a cleared value unsaved).
// Left unmapped for this ticket (no form input exists yet, so there's no
// value to send and omitting avoids clearing BFF-only data the form never
// displays):
//   - applicantPk, driverLicense, driverLicenseState
//   - current/mailing residenceOwnerTypeId, previous addresses
//   - phones[].isDoNotCall
//   - emailAddresses[].isTextOptIn
//   - employments[].workLocations (no form input for the work location object)
//   - references
//
// deleteSlots reports the trailing PRIMARY/SECONDARY/TERTIARY slot(s) that
// dropped out of the income array since the form loaded, purely as a diff
// between the original income count (from the GET hydration) and the current
// form income count — see OR-23465. Which row the analyst visually removed
// doesn't matter: removing any non-last row shifts the rest up, so the
// payload only needs to know how many slots contracted, and always reports
// the trailing ones (SECONDARY before TERTIARY).
//
// deleteSlots is typed by the generated client as Set<Enum>, but the client
// serializes request bodies with plain JSON.stringify (see step6-bff-client's
// serializeDataIfNeeded), which turns a real Set into `{}` on the wire. We
// build it as an array and cast it to the declared Set type below so the
// backend actually receives a JSON array.
// ---------------------------------------------------------------------------

// BFF phone-usage-type id: 1 = HOME, 2 = WORK, 3 = CELL, 4 = OTHER, 21 = PERSONAL.
const WORK_PHONE_USAGE_TYPE_ID = 2;
const PERSONAL_PHONE_USAGE_TYPE_ID = 21;

// Form incomeFrequencyType -> BFF incomeFrequencyTypeId.
const FREQUENCY_TO_ID: Record<NonNullable<IncomeInput["incomeFrequencyType"]>, EmploymentIncomeFrequencyTypeIdEnum> = {
  Weekly: 1,
  "Bi-Weekly": 2,
  "2x-Monthly": 3,
  Monthly: 4,
  Annual: 5,
};

/** Index-to-slot lookup for the first three income blocks; index 0 is PRIMARY. */
export const EMPLOYMENT_SLOTS_BY_INDEX = ["PRIMARY", "SECONDARY", "TERTIARY"] as const;

/**
 * Strips every non-digit character from a string.
 * @param {string} value - The raw string.
 * @returns {string} Digits only.
 */
function digitsOnly(value: string): string {
  return value.replaceAll(/\D/g, "");
}

/**
 * Narrows a form money value to a number; a cleared field ("") becomes undefined so it is omitted from the payload.
 * @param {unknown} value - The raw form value.
 * @returns {number | undefined} The number, or undefined when not a number.
 */
function numberOrUndefined(value: unknown): number | undefined {
  return typeof value === "number" ? value : undefined;
}

/**
 * Converts a blank/whitespace-only string to undefined so optional BFF fields
 * are omitted rather than sent as empty strings.
 * @param {string} value - The raw string.
 * @returns {string | undefined} The trimmed value, or undefined when blank.
 */
function undefinedIfBlank(value: string): string | undefined {
  return value.trim() === "" ? undefined : value;
}

/**
 * Maps the form customer info's physical address onto the BFF `current`
 * address slot.
 * @param {CustomerInfoInput} customerInfo - The form's customer information.
 * @returns {Address} The BFF current-address entry.
 */
export function mapCustomerInfoToCurrentAddress(customerInfo: CustomerInfoInput): Address {
  return {
    streetNumber: customerInfo.physicalStreetNumber,
    streetName: customerInfo.physicalStreetName,
    apartmentNumber: customerInfo.physicalApartmentNumber,
    zip: customerInfo.physicalZip,
    city: customerInfo.physicalCity,
    state: customerInfo.physicalState,
    // Round-trips the last verify response's Customer Hub id + USPS
    // standardization outcome (address-standardization-spec.md §5, open
    // question #4) — set by useAddressVerificationGate.ts, otherwise carried
    // through unchanged from GET hydration (residenceMapping.ts).
    addressId: customerInfo.physicalAddressId,
    addressUspsStandardType: customerInfo.physicalAddressUspsStandardType as
      | AddressAddressUspsStandardTypeEnum
      | undefined,
  };
}

/**
 * Maps the form customer info's mailing address onto the BFF `mailing`
 * address slot. When "Same as current" is checked, this is built from the
 * PHYSICAL values (not omitted) so a save always overwrites any stale
 * MAILING record on file — omitting it here would leave a previously-distinct
 * mailing address untouched server-side, which then reappears (and un-checks
 * "Same as current") on the next GET (OR-23598 follow-up).
 * @param {CustomerInfoInput} customerInfo - The form's customer information.
 * @param {Address} current - The already-mapped current/physical address.
 * @returns {Address} The BFF mailing-address entry.
 */
export function mapCustomerInfoToMailingAddress(customerInfo: CustomerInfoInput, current: Address): Address {
  return customerInfo.mailingSameAsCurrent
    ? { ...current }
    : {
        streetNumber: customerInfo.mailingStreetNumber,
        streetName: customerInfo.mailingStreetName,
        apartmentNumber: customerInfo.mailingApartmentNumber,
        zip: customerInfo.mailingZip,
        city: customerInfo.mailingCity,
        state: customerInfo.mailingState,
        addressId: customerInfo.mailingAddressId,
        addressUspsStandardType: customerInfo.mailingAddressUspsStandardType as
          | AddressAddressUspsStandardTypeEnum
          | undefined,
      };
}

/**
 * Maps a single form phone row onto a BFF phone entry. "Work" maps to usage
 * type 2; "Personal" (the form's only other option) maps to usage type 21.
 * @param {PhoneInput} phone - A form phone row.
 * @returns {Phone} The BFF phone entry.
 */
function mapPhoneRow(phone: PhoneInput): Phone {
  return {
    number: digitsOnly(phone.number),
    extension: digitsOnly(phone.extension),
    phoneUsageTypeId: phone.type === "Work" ? WORK_PHONE_USAGE_TYPE_ID : PERSONAL_PHONE_USAGE_TYPE_ID,
  };
}

/**
 * Maps the form customer info's phone rows onto the BFF phones array. Rows
 * with no digits entered are dropped.
 * @param {CustomerInfoInput} customerInfo - The form's customer information.
 * @returns {Phone[]} The BFF phone entries.
 */
export function mapCustomerInfoToPhones(customerInfo: CustomerInfoInput): Phone[] {
  return customerInfo.phones.map((phone) => mapPhoneRow(phone)).filter((phone) => (phone.number ?? "").length > 0);
}

/**
 * Maps the form customer info's email onto the BFF email-addresses array.
 * @param {CustomerInfoInput} customerInfo - The form's customer information.
 * @returns {EmailAddress[]} The BFF email entries (empty when no email is on file).
 */
export function mapCustomerInfoToEmailAddresses(customerInfo: CustomerInfoInput): EmailAddress[] {
  const emailAddress = undefinedIfBlank(customerInfo.emailAddress);
  return emailAddress ? [{ emailAddress }] : [];
}

/**
 * Maps a single form income block onto a BFF employment entry.
 * `employmentTypeId` is the numeric form of the income-type code (the schema
 * already stores it as "6".."11", matching the BFF's ids directly).
 * @param {IncomeInput} income - A form income block.
 * @param {number} displayOrder - The employment's slot order (0 = primary).
 * @returns {Employment} The BFF employment entry.
 */
function mapIncomeToEmployment(income: IncomeInput, displayOrder: number): Employment {
  return {
    displayOrder,
    isPrimary: displayOrder === 0,
    employmentTypeId: Number(income.employmentType) as EmploymentEmploymentTypeIdEnum,
    employerName: income.employerName,
    employerPhone: digitsOnly(stripMaskPlaceholder(income.employerPhone, PHONE_MASK_PLACEHOLDER)),
    phoneExtension: stripMaskPlaceholder(
      income.phoneExtension ?? "",
      EXTENSION_MASK_PLACEHOLDER,
      EXTENSION_TEXT_PLACEHOLDER
    ),
    occupation: income.occupation ?? "",
    streetNumber: income.streetNumber ?? "",
    streetName: income.streetName ?? "",
    apartmentNumber: income.apartmentNumber ?? "",
    city: income.city ?? "",
    state: income.state ?? "",
    zip: income.zip ?? "",
    hireDate: income.hireDate,
    monthlyGrossPayAmount: income.monthlyGrossPayAmount,
    incomePerPayPeriodAmount: numberOrUndefined(income.incomePerPayPeriodAmount),
    dscEnteredMonthlyIncomePerPayPeriod: numberOrUndefined(income.dscEnteredMonthlyIncomePerPayPeriod),
    monthlyIncomePerPayPeriod: income.monthlyIncomePerPayPeriod,
    incomeFrequencyTypeId: income.incomeFrequencyType ? FREQUENCY_TO_ID[income.incomeFrequencyType] : undefined,
    payType: income.payType,
    notes: income.notes ?? "",
    payStubDate: income.payStubDate ?? "",
    payPeriodEndDate: income.payPeriodEndDate ?? "",
    yearToDateIncomeAmount: income.yearToDateIncomeAmount,
  };
}

/**
 * Maps the form's income blocks onto the BFF employments array, in slot
 * order (index 0 = PRIMARY).
 * @param {IncomeInput[]} incomes - The form's income blocks (1-3).
 * @returns {Employment[]} The BFF employment entries.
 */
export function mapIncomesToEmployments(incomes: IncomeInput[]): Employment[] {
  return incomes.map((income, index) => mapIncomeToEmployment(income, index));
}

/**
 * Derives the trailing PRIMARY/SECONDARY/TERTIARY slot(s) that dropped out of
 * the income array since the form loaded — see the module comment and
 * OR-23465's count table.
 * @param {number} currentCount - The current form income count.
 * @param {number} [originalIncomeCount] - The income block count as of the last hydration or successful save.
 * @returns {ApplicantSectionWriteRequestV2DeleteSlotsEnum[] | undefined} The dropped slots, or undefined when none dropped.
 */
function deriveDeleteSlots(
  currentCount: number,
  originalIncomeCount?: number
): ApplicantSectionWriteRequestV2DeleteSlotsEnum[] | undefined {
  if (originalIncomeCount === undefined || originalIncomeCount <= currentCount) {
    return undefined;
  }
  return EMPLOYMENT_SLOTS_BY_INDEX.slice(
    currentCount,
    originalIncomeCount
  ) as ApplicantSectionWriteRequestV2DeleteSlotsEnum[];
}

/**
 * Maps the form bank account onto the BFF write-only bank fields. A blank
 * bank name is sent as an explicit `""` (the BFF clears the stored value on
 * an empty string) only when Bank Name is optional for this deal; otherwise
 * it's omitted like every other optional field, since the BFF treats an
 * explicit `""` as "clear" rather than "unset".
 * @param {BankAccountInput} bankAccount - The form's bank account.
 * @param {boolean} isBankNameOptional - True when Bank Name validation is optional for this deal.
 * @returns {Pick<ApplicantSectionWriteRequestV2, "bankName" | "isCheckingAccount" | "isSavingsAccount" | "isPrepaidDebit">} The BFF bank fields.
 */
export function mapBankAccountFields(
  bankAccount: BankAccountInput,
  isBankNameOptional: boolean
): Pick<ApplicantSectionWriteRequestV2, "bankName" | "isCheckingAccount" | "isSavingsAccount" | "isPrepaidDebit"> {
  return {
    bankName: isBankNameOptional ? (bankAccount.bankName ?? "") : undefinedIfBlank(bankAccount.bankName ?? ""),
    isCheckingAccount: bankAccount.isCheckingAccount,
    isSavingsAccount: bankAccount.isSavingsAccount,
    isPrepaidDebit: bankAccount.isPrepaidDebit,
  };
}

/**
 * Builds the full BFF `ApplicantSectionWriteRequestV2` from a form person
 * (driver or 2nd signer), for the `driver` / `secondSigner` legs of the v2
 * Dashboard-shaped save request.
 * @param {PersonInput} person - The form's driver or 2nd-signer state.
 * @param {boolean} isBankNameOptional - True when Bank Name validation is optional for this deal. See {@link mapBankAccountFields}.
 * @param {number} [originalIncomeCount] - The income block count as of the last hydration or successful save. See {@link deriveDeleteSlots}.
 * @returns {ApplicantSectionWriteRequestV2} The BFF applicant-section write request.
 */
export function buildApplicantSectionWriteRequest(
  person: PersonInput,
  isBankNameOptional: boolean,
  originalIncomeCount?: number
): ApplicantSectionWriteRequestV2 {
  const { customerInfo } = person;
  const deleteSlots = deriveDeleteSlots(person.incomes.length, originalIncomeCount);
  const current = mapCustomerInfoToCurrentAddress(customerInfo);

  return {
    firstName: customerInfo.firstName,
    middleName: customerInfo.middleInitial,
    lastName: customerInfo.lastName,
    nameSuffix: customerInfo.suffix,
    dateOfBirth: customerInfo.dateOfBirth,
    ssnOrItin: digitsOnly(customerInfo.ssnOrItin),
    current,
    mailing: mapCustomerInfoToMailingAddress(customerInfo, current),
    phones: mapCustomerInfoToPhones(customerInfo),
    emailAddresses: mapCustomerInfoToEmailAddresses(customerInfo),
    isEmailAvailable: !customerInfo.emailNotAvailable,
    employments: mapIncomesToEmployments(person.incomes),
    totalMonthlyGrossPay: person.incomes.reduce((total, income) => total + income.monthlyGrossPayAmount, 0),
    deleteSlots: deleteSlots
      ? (deleteSlots as unknown as Set<ApplicantSectionWriteRequestV2DeleteSlotsEnum>)
      : undefined,
    ...mapBankAccountFields(person.bankAccount, isBankNameOptional),
  };
}
